import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getRecurringGroupGames } from '@/integrations/supabase/game/gameQueryService';
import { fetchHoleTileData } from '@/integrations/supabase/scores/holeGridService';
import type { BirdieGameGroup } from '@/types';
import type { HoleTileData } from '@/integrations/supabase/scores';

/**
 * Fetches recurring game instances in parallel with useGameData,
 * and background-prefetches tile data for sibling instances.
 *
 * This runs a lightweight query for recurring_group_id (not the full
 * game+players+course chain), so instances and tabs appear sooner.
 */
export function useRecurringInstances(gameId?: string) {
  const [instances, setInstances] = useState<BirdieGameGroup[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const tileCacheRef = useRef<Map<string, Map<number, HoleTileData>>>(new Map());

  useEffect(() => {
    if (!gameId) return;

    let cancelled = false;
    setIsLoading(true);
    tileCacheRef.current.clear();

    const load = async () => {
      // Lightweight query: just get recurring_group_id + course_id
      const { data, error } = await supabase
        .from('games')
        .select('recurring_group_id, course_id')
        .eq('id', gameId)
        .single();

      if (cancelled) return;

      if (error || !data?.recurring_group_id) {
        setInstances([]);
        setIsLoading(false);
        return;
      }

      const groupGames = await getRecurringGroupGames(data.recurring_group_id);
      if (cancelled) return;

      setInstances(groupGames);
      setIsLoading(false);

      // Background prefetch tile data for sibling instances
      const courseId = data.course_id;
      for (const instance of groupGames) {
        if (instance.id === gameId) continue;
        fetchHoleTileData({ courseId, gameId: instance.id }).then(tileData => {
          if (!cancelled) {
            tileCacheRef.current.set(instance.id, tileData);
          }
        });
      }
    };

    load();
    return () => { cancelled = true; };
  }, [gameId]);

  const invalidateCache = useCallback((targetGameId: string) => {
    tileCacheRef.current.delete(targetGameId);
  }, []);

  const getCachedTileData = useCallback((targetGameId: string): Map<number, HoleTileData> | undefined => {
    return tileCacheRef.current.get(targetGameId);
  }, []);

  return { instances, isLoading, invalidateCache, getCachedTileData };
}
